import { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import classNames from 'classnames';
import type { LngLatLike } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { useTheme } from 'styled-components';

import { TransparentButton } from 'src/app/components/Elements/Button/TransparentButton';
import { Typography } from 'src/app/components/Elements/Typography';
import { usePlacePhoto } from 'src/app/hooks/data/pharmacy';
import type { PlaceDetail } from 'src/app/types';

import {
  Container,
  MapContainer,
  MapDetailBox,
  MapDetailImage,
  MapDetailTextContainer,
  SearchThisAreaContainer,
} from './styles';
import styles from './styles.module.scss';
import { metersBetweenLatitudes } from './utils';

interface PharmacySearchMapProps {
  selectedPharmacy: PlaceDetail;
  nearbyPlaces: PlaceDetail[];
  toggleShow: boolean;
  selectItem: (item: PlaceDetail) => void;
  onSearchThisArea: (center: string, radius: number) => void;
}

export const PharmacySearchMap = ({
  selectedPharmacy,
  toggleShow,
  nearbyPlaces,
  selectItem,
  onSearchThisArea,
}: PharmacySearchMapProps) => {
  mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_API_KEY;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);
  const mapRef = useRef<mapboxgl.Map>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const [shouldSkipReframe, setShouldSkipReframe] = useState(false);

  useEffect(() => {
    if (toggleShow) mapRef.current?.resize();
    if (!toggleShow) setShouldSkipReframe(false);
  }, [toggleShow]);

  const { data: selectedPharmacyPhoto } = usePlacePhoto(selectedPharmacy?.photos?.[0]?.photo_reference);
  const nearbyCoords = nearbyPlaces
    .slice(0, 10) // Limit nearby coordinates to 10 places at most. Otherwise, bounds usually become too large.
    .map(place => [place.geometry.location?.lng, place.geometry.location?.lat] as LngLatLike);

  /* Get the bounding box of the map. If user is searching area, don't move the box, otherwise use the location
   * of the 10 closest (the original list is sorted by zip code proximity) to define the box */
  const getBoundingBox = () => {
    if (shouldSkipReframe) {
      return mapRef.current.getBounds();
    }
    const boundingCoords = nearbyCoords.reduce(function (bounds, coord) {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds(nearbyCoords[0], nearbyCoords[0]));
    return new mapboxgl.LngLatBounds(boundingCoords._ne, boundingCoords._sw);
  };

  const fitMapToBounds = () => {
    if (nearbyCoords.length === 1) {
      mapRef.current.jumpTo({
        center: nearbyCoords[0],
        zoom: 14,
      });
    } else {
      const boundingBox = getBoundingBox();
      mapRef.current.fitBounds(boundingBox, {
        padding: { top: 140, bottom: isMobile ? 140 : 40, left: 40, right: 40 },
        animate: false,
      });
    }
  };

  const searchThisArea = () => {
    setShouldSkipReframe(true);
    const bounds = mapRef.current.getBounds();

    const { lng, lat } = bounds.getCenter();
    const center = `${lat},${lng}`;

    const northLat = bounds._ne.lat;
    const southLat = bounds._sw.lat;
    const radius = metersBetweenLatitudes(northLat, southLat);

    onSearchThisArea(center, radius);
    setShowSearchAreaButton(false);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (nearbyPlaces.length === 0) return;
    const map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/capsulecares/ckitejqpf1bhf1arzlx8q5sq5',
      center: getBoundingBox().getCenter(),
      zoom: 13,
    });
    mapRef.current = map;
    mapRef.current.addControl(new mapboxgl.NavigationControl({ showZoom: true, showCompass: false }), 'top-right');
    mapRef.current.on('load', () => {
      mapRef.current.resize();
      nearbyPlaces.map(place => {
        const el = document.createElement('button');
        el.onclick = e => {
          e.preventDefault();
          setShouldSkipReframe(true);
          setShowSearchAreaButton(false);
          mapRef.current?.jumpTo({
            center: [place.geometry.location?.lng, place.geometry?.location?.lat],
            zoom: mapRef.current.getZoom(),
          });
          selectItem(place);
        };
        el.className = classNames(
          styles.pharmacyMarker,
          place.place_id === selectedPharmacy.place_id ? styles.selectedMarker : undefined
        );
        el.ariaLabel = `${place.name}, ${place.formatted_address}`;
        new mapboxgl.Marker(el, { anchor: 'bottom' })
          .setLngLat([place.geometry.location?.lng, place.geometry.location?.lat])
          .addTo(mapRef.current);
      });
    });
    fitMapToBounds();

    // Resize Map on Window Resize
    window.addEventListener('resize', fitMapToBounds);

    mapRef.current.on('dragend', () => setShowSearchAreaButton(true));
    mapRef.current.on('zoomend', () => setShowSearchAreaButton(true));

    // Remove Resize Listener
    return () => {
      window.addEventListener('resize', fitMapToBounds);
      // Unmount map to avoid having two concurrent maps.
      map.remove();
    };
  }, [nearbyPlaces]);

  return (
    <Container className={classNames('pharmacy-container')}>
      <MapContainer id="map" ref={mapContainerRef} />

      <SearchThisAreaContainer showSearchAreaButton={showSearchAreaButton}>
        <TransparentButton onClick={searchThisArea}>
          <Typography variant="overline" color={'capsuleGreen60'}>
            Search This Area
          </Typography>
        </TransparentButton>
      </SearchThisAreaContainer>

      {selectedPharmacy?.place_id && (
        <MapDetailBox>
          <MapDetailImage imageSrc={selectedPharmacyPhoto?.data || ''} />
          <MapDetailTextContainer>
            <Typography variant="body" marginBottom={'s1'} color={'capsuleGreen60'}>
              {selectedPharmacy.name}
            </Typography>
            <Typography variant="small" marginBottom={'none'} color={'capsuleGray50'}>
              {selectedPharmacy.formatted_address}
            </Typography>
          </MapDetailTextContainer>
        </MapDetailBox>
      )}
    </Container>
  );
};
